import { useEffect } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { ArrowLeft, ShieldCheck, Wrench } from 'lucide-react';
import { usePaymentStore } from '../state/usePaymentStore';
import { usePaymentGate } from '../hooks/usePaymentGate';
import { PlanCard } from '../components/PlanCard';
import { trackPlansViewed, trackAddToCart } from '../services/paymentAnalytics';

/**
 * ─── Écran : Choix du plan ───────────────────────────────────────────────────
 * Si les paiements sont désactivés (admin), la page n'existe pas pour
 * l'utilisateur : redirection silencieuse vers le dashboard.
 */
export function PlansPage() {
  const navigate = useNavigate();
  const { paymentsEnabled, canPurchaseInApp } = usePaymentGate();
  const config = usePaymentStore((s) => s.config);
  const allPlans = usePaymentStore((s) => s.plans);
  const subscription = usePaymentStore((s) => s.subscription);
  const plans = allPlans.filter((p) => p.isActive);

  // Parcours d'achat GA4 : vue de la liste des plans (dont Premium) au 1er rendu
  // avec des plans disponibles. Clé = ids des plans pour ne loguer qu'une fois.
  const plansKey = plans.map((p) => p.id).join(',');
  useEffect(() => {
    if (paymentsEnabled && canPurchaseInApp && plans.length > 0) trackPlansViewed(plans);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentsEnabled, canPurchaseInApp, plansKey]);

  if (!paymentsEnabled || !canPurchaseInApp) return <Navigate to="/dashboard" replace />;

  return (
    <div className="min-h-screen bg-app px-4 py-8">
      <div className="max-w-3xl mx-auto">
        <button
          onClick={() => navigate('/dashboard')}
          className="inline-flex items-center gap-1.5 text-xs text-fg-faint hover:text-fg-subtle transition-colors mb-6"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Retour
        </button>

        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-fg mb-2">Choisissez votre plan</h1>
          <p className="text-sm text-fg-subtle">
            Abonnement mensuel, sans engagement. Annulez à tout moment.
          </p>
        </div>

        {config.maintenanceMode && (
          <div className="flex items-center gap-3 bg-amber-500/10 border border-amber-500/30 text-amber-400 rounded-xl px-4 py-3 text-sm mb-6">
            <Wrench className="w-4 h-4 flex-shrink-0" />
            Les paiements sont temporairement en maintenance. Vous pourrez vous
            abonner de nouveau très bientôt.
          </div>
        )}

        <div className="grid sm:grid-cols-2 gap-5 sm:gap-6 pt-3">
          {plans.map((plan) => (
            <PlanCard
              key={plan.id}
              plan={plan}
              isCurrent={
                subscription?.status === 'active' && subscription.planId === plan.id
              }
              disabled={config.maintenanceMode}
              onSelect={(p) => {
                trackAddToCart(p);
                navigate(`/checkout/${p.id}`);
              }}
            />
          ))}
        </div>

        <div className="mt-8 flex flex-col items-center gap-2">
          <p className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-sm font-medium text-fg-subtle">
            <span className="rounded-md bg-overlay-1 px-2 py-1">Airtel Money</span>
            <span className="rounded-md bg-overlay-1 px-2 py-1">Orange Money</span>
            <span className="rounded-md bg-overlay-1 px-2 py-1">M-Pesa</span>
            <span className="rounded-md bg-overlay-1 px-2 py-1">Africell</span>
            <span className="rounded-md bg-overlay-1 px-2 py-1">Carte bancaire</span>
          </p>
          <p className="flex items-center justify-center gap-2 text-xs text-fg-faint">
            <ShieldCheck className="w-4 h-4" />
            Paiement sécurisé — payez avec votre Mobile Money, sans carte bancaire
          </p>
        </div>
      </div>
    </div>
  );
}
